import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Loader2, RefreshCw, Mail, Inbox } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

const MessagesTab = ({ token }) => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);

  const reload = useCallback(() => setReloadTick((t) => t + 1), []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const res = await axios.get(`${API}/contact/list?limit=2000`, {
          headers: { "X-Admin-Token": token },
        });
        if (!cancelled) {
          setItems(res.data.items || []);
          setTotal(res.data.total || 0);
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          toast.error("Failed to load messages");
          setLoading(false);
        }
      }
    };
    setLoading(true);
    run();
    return () => { cancelled = true; };
  }, [token, reloadTick]);

  const downloadCsv = async () => {
    try {
      const res = await axios.get(`${API}/contact/export.csv`, {
        headers: { "X-Admin-Token": token },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const dispo = res.headers["content-disposition"] || "";
      const m = dispo.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : "gigafloat-messages.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  if (loading && items.length === 0) {
    return (
      <div className="py-20 text-center text-white/40">
        <Loader2 className="w-6 h-6 animate-spin inline" />
      </div>
    );
  }

  return (
    <div data-testid="messages-tab">
      <div className="flex items-center justify-between mb-6 gap-3 flex-wrap">
        <div className="inline-flex items-center gap-2 text-white/60 text-sm">
          <Inbox className="w-4 h-4" /> {total.toLocaleString()} messages
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={reload}
            className="inline-flex items-center gap-2 text-sm text-white/70 hover:text-white border border-white/10 hover:border-white/30 rounded-full px-4 py-2 transition-colors"
            data-testid="messages-refresh"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button
            onClick={downloadCsv}
            className="inline-flex items-center gap-2 bg-[#00FF88] text-black font-bold text-sm px-5 py-2 rounded-full btn-glow"
            data-testid="messages-export-csv"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="py-20 text-center text-white/40 glass rounded-2xl">
          <Mail className="w-6 h-6 inline mb-2" />
          <div>No messages yet.</div>
        </div>
      ) : (
        <div className="space-y-3" data-testid="messages-list">
          {items.map((m) => {
            const open = openId === m.id;
            return (
              <div
                key={m.id}
                className="rounded-2xl border border-white/8 bg-white/[0.02] overflow-hidden"
                data-testid={`message-row-${m.id}`}
              >
                <button
                  type="button"
                  onClick={() => setOpenId(open ? null : m.id)}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-white/[0.02] transition-colors"
                >
                  <div className="min-w-0">
                    <div className="font-display font-extrabold tracking-tight truncate">
                      {m.subject}
                    </div>
                    <div className="text-xs text-white/45 font-mono-c truncate">
                      {m.first_name} {m.last_name || ""} · {m.email}
                    </div>
                  </div>
                  <div className="text-[11px] font-mono-c text-white/40 shrink-0">
                    {fmt(m.created_at)}
                  </div>
                </button>
                {open && (
                  <div className="px-5 pb-5 space-y-3">
                    <div className="text-white/80 text-sm leading-relaxed whitespace-pre-wrap">
                      {m.message}
                    </div>
                    <div className="grid sm:grid-cols-3 gap-3 text-[11px] font-mono-c text-white/50">
                      <div>
                        <div className="text-white/35 uppercase tracking-widest mb-0.5">Source page</div>
                        <div>{m.source_page || "—"}</div>
                      </div>
                      <div>
                        <div className="text-white/35 uppercase tracking-widest mb-0.5">IP</div>
                        <div>{m.ip || "—"}</div>
                      </div>
                      <div>
                        <div className="text-white/35 uppercase tracking-widest mb-0.5">Agent</div>
                        <div className="truncate" title={m.user_agent || ""}>{m.user_agent || "—"}</div>
                      </div>
                    </div>
                    <a
                      href={`mailto:${m.email}?subject=Re:%20${encodeURIComponent(m.subject)}`}
                      className="inline-flex items-center gap-2 bg-[#00FF88] text-black font-bold text-sm px-5 py-2 rounded-full btn-glow"
                      data-testid={`message-reply-${m.id}`}
                    >
                      <Mail className="w-4 h-4" />
                      Reply
                    </a>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MessagesTab;
